import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Volume2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedPack() {
  const { shareId } = useParams();
  const [pack, setPack] = useState(null);
  const [missing, setMissing] = useState(false);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/shared/packs/${shareId}`)
      .then(({ data }) => setPack(data))
      .catch(() => setMissing(true));
  }, [shareId]);

  const play = async (index, slow) => {
    const key = `${index}:${slow ? "slow" : "normal"}`;
    if (playing) return;
    setPlaying(key);
    const audio = new Audio(`${API}/shared/packs/${shareId}/speech?i=${index}&slow=${slow}`);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => setPlaying(null);
    try {
      await audio.play();
    } catch {
      setPlaying(null);
    }
  };

  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="shared-pack-missing">
        <p className="text-sm text-ink-soft">This pack link is no longer available.</p>
        <Link to="/" className="mt-6 inline-flex text-sm text-brand hover:underline">Go to ChatMiLu</Link>
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24" data-testid="shared-pack-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8" data-testid="shared-pack-page">
      <Link to="/">
        <img src="/logo.png" alt="ChatMiLu" className="h-10 w-auto" />
      </Link>

      <p className="mt-10 text-xs uppercase tracking-[0.2em] text-ink-soft" data-testid="shared-pack-by">
        {pack.shared_by} shared a phrase pack with you
      </p>
      <h1 data-testid="shared-pack-title" className="mt-3 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
        {pack.title}
      </h1>
      <p className="mt-1 text-sm text-ink-soft">{pack.blurb} · {pack.phrases.length} phrases</p>

      <ul className="mt-6 space-y-2" data-testid="shared-pack-list">
        {pack.phrases.map((p, i) => (
          <li
            key={`${i}-${p.text}`}
            className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-panel px-4 py-3"
          >
            <span className="min-w-0">
              <span dir="auto" className="block font-medium">{p.text}</span>
              <span className="block text-xs text-ink-soft">
                {p.en}
                {p.say ? ` · ${p.say}` : ""}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              <button
                data-testid={`shared-pack-speak-${i}`}
                onClick={() => play(i, false)}
                aria-label={`Hear ${p.text}`}
                className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
              >
                <Volume2 className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`shared-pack-slow-${i}`}
                onClick={() => play(i, true)}
                aria-label={`Hear ${p.text} slowly`}
                className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold tracking-wide text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
              >
                {playing === `${i}:slow` ? "…" : "SLOW"}
              </button>
            </span>
          </li>
        ))}
      </ul>

      <Link
        to="/signup"
        data-testid="shared-pack-cta"
        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline"
      >
        Get all 50 languages with ChatMiLu <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
